import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ClipboardList, Loader2, Clock, PenTool, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { PortalFirmarDialog } from '@/components/portal/PortalFirmarDialog';

// Solo se seleccionan columnas seguras para el cliente
// (no se exponen costes internos ni notas internas)
const SELECT_COLS = `
  id,
  numero_parte,
  fecha_trabajo,
  hora_inicio,
  hora_fin,
  duracion_total,
  descripcion_trabajo,
  tipo_intervencion,
  resultado_intervencion,
  requiere_nueva_visita,
  motivo_no_resuelto,
  materiales_necesarios,
  direccion_intervencion,
  poblacion_intervencion,
  codigo_postal_intervencion,
  estado,
  firma_cliente,
  nombre_firmante_cliente,
  fecha_firma_cliente,
  created_at
`;

function formatDuracion(intervalStr: string | null): string {
  if (!intervalStr) return '—';
  const m = String(intervalStr).match(/(\d+):(\d+):(\d+)/);
  if (m) {
    const h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    if (h === 0) return `${min} min`;
    if (min === 0) return `${h} h`;
    return `${h} h ${min} min`;
  }
  return String(intervalStr);
}

export default function PortalPartes() {
  const [partes, setPartes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [firmando, setFirmando] = useState<any | null>(null);

  async function cargar() {
    const { data } = await (supabase as any)
      .from('partes_trabajo')
      .select(SELECT_COLS)
      .or('archivado.is.null,archivado.eq.false')
      .order('fecha_trabajo', { ascending: false });
    setPartes((data ?? []) as any[]);
    setLoading(false);
  }

  useEffect(() => {
    cargar();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {partes.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No tienes partes de trabajo todavía.
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3">
        {partes.map((p) => (
          <Card key={p.id}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <ClipboardList className="h-4 w-4" />
                    Parte de trabajo · {p.fecha_trabajo ? format(new Date(p.fecha_trabajo), "d 'de' MMMM yyyy", { locale: es }) : '—'}
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">
                    Nº {p.numero_parte || '—'}
                  </p>
                </div>
                {p.tipo_intervencion && (
                  <Badge variant="secondary" className="capitalize">
                    {String(p.tipo_intervencion).replace(/_/g, ' ')}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {p.descripcion_trabajo && (
                <div>
                  <p className="text-muted-foreground text-xs">Trabajo realizado</p>
                  <p className="whitespace-pre-wrap">{p.descripcion_trabajo}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                {(p.hora_inicio || p.hora_fin) && (
                  <div>
                    <p className="text-muted-foreground text-xs flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Horario
                    </p>
                    <p>
                      {p.hora_inicio ? p.hora_inicio.slice(0, 5) : '—'}
                      {' → '}
                      {p.hora_fin ? p.hora_fin.slice(0, 5) : '—'}
                    </p>
                  </div>
                )}
                {p.duracion_total && (
                  <div>
                    <p className="text-muted-foreground text-xs">Duración</p>
                    <p>{formatDuracion(p.duracion_total)}</p>
                  </div>
                )}
              </div>

              {p.direccion_intervencion && (
                <div>
                  <p className="text-muted-foreground text-xs">Lugar de intervención</p>
                  <p>
                    {p.direccion_intervencion}
                    {(p.codigo_postal_intervencion || p.poblacion_intervencion) && (
                      <>
                        {' · '}
                        {[p.codigo_postal_intervencion, p.poblacion_intervencion]
                          .filter(Boolean)
                          .join(' ')}
                      </>
                    )}
                  </p>
                </div>
              )}

              {p.materiales_necesarios && (
                <div>
                  <p className="text-muted-foreground text-xs">Materiales</p>
                  <p className="whitespace-pre-wrap">{p.materiales_necesarios}</p>
                </div>
              )}

              {p.resultado_intervencion && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge
                    variant="outline"
                    className={
                      p.resultado_intervencion === 'resuelto'
                        ? 'bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30'
                        : p.resultado_intervencion === 'no_resuelto'
                        ? 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30'
                        : ''
                    }
                  >
                    {String(p.resultado_intervencion).replace(/_/g, ' ')}
                  </Badge>
                  {p.requiere_nueva_visita && (
                    <Badge variant="outline" className="bg-yellow-500/15 text-yellow-700 dark:text-yellow-300 border-yellow-500/30">
                      Requiere nueva visita
                    </Badge>
                  )}
                </div>
              )}

              {p.motivo_no_resuelto && (
                <div>
                  <p className="text-muted-foreground text-xs">Motivo</p>
                  <p className="whitespace-pre-wrap">{p.motivo_no_resuelto}</p>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
                {p.firma_cliente ? (
                  <Badge variant="outline" className="bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Firmado{p.nombre_firmante_cliente ? ` por ${p.nombre_firmante_cliente}` : ''}
                    {p.fecha_firma_cliente ? ` · ${format(new Date(p.fecha_firma_cliente), 'd MMM yyyy', { locale: es })}` : ''}
                  </Badge>
                ) : (
                  <Button size="sm" onClick={() => setFirmando(p)}>
                    <PenTool className="h-4 w-4 mr-2" />
                    Firmar conformidad
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {firmando && (
        <PortalFirmarDialog
          open={!!firmando}
          onOpenChange={(o) => !o && setFirmando(null)}
          tabla="partes_trabajo"
          documentoId={firmando.id}
          numeroDocumento={`Parte de trabajo · ${firmando.fecha_trabajo ? format(new Date(firmando.fecha_trabajo), "d 'de' MMMM yyyy", { locale: es }) : ''}`}
          tituloDocumento="parte de trabajo"
          onFirmado={cargar}
        />
      )}
    </div>
  );
}
